/**
 * Tracking de eventos hacia el backend.
 *
 * Best-effort: nunca rompe el flow de la UI. Bufferea eventos en memoria y
 * los flushea cada 5s o al unload de la página.
 *
 * Uso:
 *   track({ type: 'property_view', category: 'client', action: 'view', resourceId: 'PROP-123' });
 *   track({ type: 'action', category: 'agent', action: 'create_customer' });
 *
 * Para silenciar en dev: `localStorage.setItem('analytics:disabled', '1')`.
 */

type Category = 'agent' | 'client' | 'system';

type Type =
  | 'page_view' | 'action' | 'property_view' | 'search' | 'gustavo_chat'
  | 'gustavo_tool' | 'cta_click' | 'funnel_step' | 'error' | 'auth';

export interface AnalyticsEventInput {
  type:     Type;
  category: Category;
  action:   string;
  label?:   string;
  value?:   number;
  path?:    string;
  referrer?: string;
  resourceType?: string;
  resourceId?:   string;
  meta?:    Record<string, unknown>;
}

const BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:4001/api/v2';
const ENDPOINT = `${BASE}/tracking/track`;
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 30;
const STORAGE_DISABLE_KEY = 'analytics:disabled';
const SESSION_STORAGE_KEY = 'imperium_analytics_session_id';

let buffer: Array<AnalyticsEventInput & { ts: number; path?: string; referrer?: string; sessionId?: string }> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

/**
 * En desarrollo NO se manda telemetría. El dev server proxea `/api` contra el backend
 * de PRODUCCIÓN, así que sin este corte los errores de código a medio escribir entran
 * al panel de analítica real: el 11/08 un `ReferenceError` en el cerebro de Gustavo,
 * en loop de render con HMR, metió 437 eventos `crash` en siete segundos y disparó la
 * alerta de "errores accionables" como si producción se estuviera cayendo.
 *
 * El backend también los descarta, pero por sí solo no alcanza: el proxy de Vite
 * reescribe el header `Origin` al dominio de producción (ver vite.config.ts) y ahí el
 * filtro de la ingesta deja de reconocer que el tráfico es local.
 */
function esEntornoLocal(): boolean {
  if (typeof window === 'undefined') return false;
  return /^(localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0)$/i.test(window.location.hostname);
}

function isDisabled(): boolean {
  if (esEntornoLocal()) return true;
  try { return localStorage.getItem(STORAGE_DISABLE_KEY) === '1'; } catch { return false; }
}

/**
 * sessionId estable durante la pestaña abierta — reusa sessionStorage para que
 * el agente que abre múltiples tabs tenga sesiones independientes (más útil
 * para reproducir bugs).
 */
export function getAnalyticsSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return `sess-${Date.now()}`;
  }
}

function getAuthToken(): string | undefined {
  // El token está en el slice auth persistido bajo "imperium-v2"
  try {
    const raw = localStorage.getItem('persist:imperium-v2');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.auth === 'string') {
      const auth = JSON.parse(parsed.auth);
      return auth?.accessToken;
    }
  } catch { /* ignore */ }
  return undefined;
}

async function flush(useBeacon = false): Promise<void> {
  if (buffer.length === 0) return;
  const events = buffer.splice(0, MAX_BATCH);
  const token = getAuthToken();

  /**
   * navigator.sendBeacon es la forma robusta para flushear en page unload — pero no admite
   * headers, así que el token va EN EL BODY. Sin esto, la última tanda de cada agente
   * llegaba anónima y el mapa de soporte lo contaba como un visitante web más, al lado de
   * su propia foto. (10/09/2026)
   */
  if (useBeacon && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
    try {
      navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify({ events, ...(token ? { token } : {}) })], { type: 'application/json' }));
      return;
    } catch { /* fall through to fetch */ }
  }

  const body = JSON.stringify({ events });
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    await fetch(ENDPOINT, {
      method:      'POST',
      credentials: 'include',
      keepalive:   useBeacon,
      headers,
      body,
    });
  } catch {
    // Best-effort: no reintentamos para no acumular.
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

function installFlushOnUnload() {
  if (typeof window === 'undefined' || installed) return;
  installed = true;

  // pagehide es más fiable que beforeunload en mobile/safari
  window.addEventListener('pagehide', () => { void flush(true); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}

/** Encola un evento. Se manda al backend de forma diferida. */
export function track(event: AnalyticsEventInput): void {
  if (isDisabled()) return;
  installFlushOnUnload();

  buffer.push({
    ...event,
    ts:        Date.now(),
    path:      event.path     ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
    referrer:  event.referrer ?? (typeof document !== 'undefined' ? document.referrer : undefined),
    sessionId: getAnalyticsSessionId(),
  });

  if (buffer.length >= MAX_BATCH) {
    void flush();
  } else {
    scheduleFlush();
  }
}

/** Forzar el flush inmediato. Útil después de un logout o un evento crítico. */
export function flushAnalytics(): Promise<void> {
  return flush();
}

/**
 * ¿La app está corriendo INSTALADA (ícono en la pantalla de inicio) o en el navegador?
 *
 * Tres señales, porque ninguna sola cubre todos los casos:
 *  · `display-mode: standalone` — la respuesta directa del navegador. Android y escritorio.
 *  · `navigator.standalone` — lo único que responde Safari en iOS, que no soporta la media query.
 *  · `?source=pwa` — el `start_url` del manifiesto. Cubre el arranque incluso si las otras
 *    dos fallan, y sobrevive a la navegación porque lo guardamos en la sesión.
 */
const PWA_SESSION_KEY = 'imperium_launch_pwa';

function llegoPorStartUrl(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('source') === 'pwa') {
      sessionStorage.setItem(PWA_SESSION_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(PWA_SESSION_KEY) === '1';
  } catch { return false; }
}

export function estaInstalada(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode = window.matchMedia?.('(display-mode: standalone)')?.matches === true;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone || llegoPorStartUrl();
}

/**
 * Un evento por sesión con el modo de apertura. Sirve para saber quién tiene el CRM
 * instalado en el celular: hasta ahora se inferían de la suscripción de notificaciones,
 * que en Android no prueba nada (Chrome permite push sin instalar).
 *
 * Uno por sesión a propósito: para contar personas no hace falta repetirlo en cada
 * navegación, y el volumen de analytics_events ya es alto.
 */
const MODO_SESSION_KEY = 'imperium_launch_tracked';

export function trackAppOpen(): void {
  if (typeof window === 'undefined' || isDisabled()) return;
  // El evento sirve para saber QUIÉN tiene la app instalada, así que necesita ir
  // atribuido. Si todavía no hay sesión (pantalla de login) se manda igual —cuenta
  // para el agregado— pero NO se marca la sesión, así vuelve a salir ya autenticado
  // cuando el agente entra. Si no, la apertura desde el ícono quedaba siempre anónima.
  const autenticado = !!getAuthToken();
  try {
    if (sessionStorage.getItem(MODO_SESSION_KEY) === '1') return;
    if (autenticado) sessionStorage.setItem(MODO_SESSION_KEY, '1');
  } catch { /* sin sessionStorage se manda igual: mejor repetido que ausente */ }

  const instalada = estaInstalada();
  track({
    type:     'action',
    category: 'system',
    action:   'app_open',
    label:    instalada ? 'pwa' : 'browser',
    meta: {
      displayMode:    window.matchMedia?.('(display-mode: standalone)')?.matches === true,
      iosStandalone:  (window.navigator as { standalone?: boolean }).standalone === true,
      startUrlPwa:    llegoPorStartUrl(),
      // Sin el UA completo: el backend ya lo guarda y lo clasifica por su cuenta.
      touch:          (navigator.maxTouchPoints ?? 0) > 0,
    },
  });
}

/**
 * Instala listeners globales para capturar errores de JS no manejados:
 *  - Errores síncronos (window.onerror)
 *  - Promesas rechazadas no atrapadas (unhandledrejection)
 *
 * Llamar UNA vez al boot de la app. No bloquea ni demora — todo va por track().
 */
// Errores BENIGNOS de librerías que no son incidencias reales y sólo ensucian el panel:
//  - html2canvas (captura de pantalla del "Reportar bug") lanza un rechazo async suelto
//    cuando un elemento con background-image se renderiza a un canvas 0x0 → createPattern
//    tira InvalidStateError en renderBackgroundImage. La captura ya degrada elegante
//    (try/catch en ReportBugDialog), así que no es un crash: no lo reportamos.
function isIgnorableClientError(text: string): boolean {
  // Ruido conocido de canvas.
  if (/createPattern|renderBackgroundImage/i.test(text)) return true;
  // Errores inyectados por EXTENSIONES del navegador (MetaMask y otras wallets, ad-blockers,
  // etc.): el stack apunta a chrome-extension:// / moz-extension:// — no es código de la app,
  // no lo controlamos y ensucia el panel. (ej. "Failed to connect to MetaMask").
  if (/(chrome|moz|safari-web)-extension:\/\//i.test(text)) return true;
  if (/Failed to connect to MetaMask/i.test(text)) return true;
  return false;
}

let globalErrorsInstalled = false;
export function installGlobalErrorTracking() {
  if (globalErrorsInstalled || typeof window === 'undefined') return;
  globalErrorsInstalled = true;

  window.addEventListener('error', (event) => {
    if (isDisabled()) return;
    // Filtrar errores de recursos (imágenes 404, etc.) y los provocados por extensiones
    if (!event.error && !event.message) return;
    if (isIgnorableClientError(`${event.message ?? ''} ${event.error?.stack ?? ''}`)) return;

    track({
      type:     'error',
      category: 'system',
      action:   'window_error',
      label:    String(event.message ?? event.error?.message ?? 'unknown').slice(0, 200),
      meta: {
        filename: event.filename,
        lineno:   event.lineno,
        colno:    event.colno,
        stack:    String(event.error?.stack ?? '').slice(0, 1000),
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isDisabled()) return;
    const reason = (event as PromiseRejectionEvent).reason;
    if (isIgnorableClientError(`${reason?.message ?? reason ?? ''} ${reason?.stack ?? ''}`)) return;
    track({
      type:     'error',
      category: 'system',
      action:   'unhandled_rejection',
      label:    String(reason?.message ?? reason ?? 'unknown').slice(0, 200),
      meta: {
        stack: String(reason?.stack ?? '').slice(0, 1000),
      },
    });
  });
}
